/**
 * MarketplaceRoute — /marketplace listing page.
 *
 * Renders the professional grid, category filter, service-area filter,
 * search input (fires ?q=<query> per backend contract — NOT ?search=),
 * pagination via ?page + ?page_size, plus loading / empty states.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, RefreshCw, X } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AppNav from "@/components/app/AppNav";
import ProfessionalCard from "@/components/marketplace/ProfessionalCard";

const PAGE_SIZE_DEFAULT = 12;
const PAGE_SIZE_MAX = 100;

// US state list (2-letter codes) — kept static so the filter never
// depends on an extra API call.
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS",
  "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK",
  "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV",
  "WI", "WY",
];

function SkeletonCard() {
  return (
    <div
      className="animate-pulse rounded-[var(--ds-radius-card)] p-5"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border))",
        minHeight: 200,
      }}
    >
      <div className="h-4 w-3/4 rounded bg-white/10 mb-3" />
      <div className="h-3 w-1/3 rounded bg-white/5 mb-4" />
      <div className="h-3 w-full rounded bg-white/5 mb-2" />
      <div className="h-3 w-11/12 rounded bg-white/5 mb-2" />
      <div className="h-3 w-9/12 rounded bg-white/5" />
    </div>
  );
}

export default function MarketplaceRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const category = searchParams.get("category") || "";
  const state = (searchParams.get("state") || "").toUpperCase();
  const q = searchParams.get("q") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const pageSize = Math.min(
    PAGE_SIZE_MAX,
    Math.max(1, parseInt(searchParams.get("page_size") || String(PAGE_SIZE_DEFAULT), 10) || PAGE_SIZE_DEFAULT)
  );

  const [qDraft, setQDraft] = useState(q);
  useEffect(() => {
    setQDraft(q);
  }, [q]);

  // Load categories once.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/marketplace/categories")
      .then((r) => {
        if (!cancelled) setCategories(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPros = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { limit: pageSize, skip: (page - 1) * pageSize };
    if (category) params.category = category;
    if (state) params.state = state;
    if (q) params.q = q;
    api
      .get("/marketplace/professionals", { params })
      .then((r) => setPros(r.data || []))
      .catch((err) => {
        setPros([]);
        setError(formatApiError(err));
      })
      .finally(() => setLoading(false));
  }, [category, state, q, page, pageSize]);

  useEffect(() => {
    fetchPros();
  }, [fetchPros]);

  function updateParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // Any filter change resets pagination.
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    const next = new URLSearchParams();
    setSearchParams(next, { replace: true });
    setQDraft("");
  }

  function submitSearch(e) {
    e.preventDefault();
    updateParam("q", qDraft.trim());
  }

  const hasFilters = Boolean(category || state || q);
  const canGoPrev = page > 1;
  const canGoNext = pros.length === pageSize;

  const categoryOptions = useMemo(() => {
    // Sort by display_name asc (already server-sorted, but be defensive).
    return [...categories].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [categories]);

  return (
    <div
      data-testid="marketplace-root"
      className="min-h-screen"
      style={{ background: "var(--ds-color-background)", color: "var(--ds-color-ink)" }}
    >
      <AppNav />
      <main className="mx-auto max-w-6xl px-4 md:px-8 py-8 md:py-12 space-y-6">
        <header className="space-y-2">
          <div
            className="text-[11px] uppercase tracking-[0.22em] font-semibold"
            style={{ color: "var(--ds-color-accent-strong)" }}
          >
            Professional marketplace
          </div>
          <h1
            className="text-[30px] md:text-[36px] font-semibold leading-tight"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Find the right professional
          </h1>
          <p className="text-sm md:text-base max-w-2xl" style={{ color: "var(--ds-color-ink-muted)" }}>
            Browse grant writers, CPAs, and nonprofit consultants. Every listing
            you'll see today marked "Demo Profile" is here to show what real
            listings will look like — real professionals are joining soon.
          </p>
        </header>

        {/* Filter bar */}
        <div
          className="rounded-[var(--ds-radius-card)] p-4 md:p-5 grid gap-3 md:grid-cols-[1fr_200px_180px_auto] items-stretch"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border))",
          }}
        >
          <form onSubmit={submitSearch} className="relative">
            <label htmlFor="marketplace-search" className="sr-only">
              Search professionals
            </label>
            <Search
              className="absolute top-1/2 -translate-y-1/2 left-3 h-4 w-4"
              style={{ color: "var(--ds-color-ink-muted)" }}
              aria-hidden="true"
            />
            <input
              id="marketplace-search"
              data-testid="marketplace-search-input"
              type="search"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="Search by name or specialty"
              className="w-full rounded-md pl-9 pr-3 py-2 text-sm bg-transparent border"
              style={{ borderColor: "var(--ds-color-border))" }}
            />
          </form>

          <label className="sr-only" htmlFor="marketplace-category-filter">Filter by category</label>
          <select
            id="marketplace-category-filter"
            data-testid="marketplace-category-filter"
            value={category}
            onChange={(e) => updateParam("category", e.target.value)}
            className="rounded-md px-3 py-2 text-sm bg-transparent border"
            style={{ borderColor: "var(--ds-color-border))" }}
          >
            <option value="">All categories</option>
            {categoryOptions.map((cat) => (
              <option key={cat.slug} value={cat.slug}>
                {cat.display_name}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor="marketplace-state-filter">Filter by state</label>
          <select
            id="marketplace-state-filter"
            data-testid="marketplace-state-filter"
            value={state}
            onChange={(e) => updateParam("state", e.target.value)}
            className="rounded-md px-3 py-2 text-sm bg-transparent border"
            style={{ borderColor: "var(--ds-color-border))" }}
          >
            <option value="">Any service area</option>
            {US_STATES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2 justify-end">
            {hasFilters && (
              <button
                type="button"
                data-testid="marketplace-clear-filters"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-md border"
                style={{ borderColor: "var(--ds-color-border))" }}
              >
                <X className="h-3 w-3" aria-hidden="true" /> Clear
              </button>
            )}
            <button
              type="button"
              data-testid="marketplace-refresh-btn"
              onClick={fetchPros}
              aria-label="Refresh results"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-md border"
              style={{ borderColor: "var(--ds-color-border))" }}
            >
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Results */}
        {error && (
          <div
            data-testid="marketplace-error"
            role="alert"
            className="rounded-md p-4 text-sm"
            style={{
              background: "var(--ds-color-danger-soft)",
              border: "1px solid var(--ds-color-danger-soft)",
              color: "var(--ds-color-ink)",
            }}
          >
            <div className="font-semibold mb-1">Marketplace temporarily unavailable</div>
            <div>{error}</div>
          </div>
        )}

        {loading ? (
          <div
            data-testid="marketplace-loading"
            className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : pros.length === 0 ? (
          <div
            data-testid="marketplace-empty-state"
            className="rounded-[var(--ds-radius-card)] p-8 text-center"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px solid var(--ds-color-border))",
            }}
          >
            <Filter
              className="mx-auto mb-3 h-6 w-6"
              style={{ color: "var(--ds-color-ink-muted)" }}
              aria-hidden="true"
            />
            <div className="text-lg font-semibold mb-1" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
              No professionals match your filters
            </div>
            <p className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>
              Try broadening your search — clear a filter or search a different specialty.
            </p>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                data-testid="marketplace-empty-clear-btn"
                className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-md"
                style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {pros.map((pro) => (
              <ProfessionalCard key={pro.id} pro={pro} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {(canGoPrev || canGoNext) && !loading && pros.length > 0 && (
          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              type="button"
              data-testid="marketplace-page-prev"
              disabled={!canGoPrev}
              onClick={() => updateParam("page", String(page - 1))}
              className="px-4 py-2 text-sm font-medium rounded-md border disabled:opacity-40"
              style={{ borderColor: "var(--ds-color-border))" }}
            >
              Previous
            </button>
            <span
              data-testid="marketplace-page-indicator"
              className="text-sm"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Page {page}
            </span>
            <button
              type="button"
              data-testid="marketplace-page-next"
              disabled={!canGoNext}
              onClick={() => updateParam("page", String(page + 1))}
              className="px-4 py-2 text-sm font-medium rounded-md border disabled:opacity-40"
              style={{ borderColor: "var(--ds-color-border))" }}
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
